import { useState } from 'react'
import type { ApiResponse } from '../../../../apiLayer/types'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { describeJoinCallFailure, showCallInTab, type ControlTowerActionOutcome } from './controlTowerHelpers'

type CallLinkAnswer = ApiResponse<{ roomUrl: string }>

// What the request for a call's link reports back: its answer, or that it failed.
type CallLinkCallbacks = { onSuccess: (response: CallLinkAnswer) => void; onError: (error: Error) => void }

/** Opens a call in a new tab once its link arrives; the tab is opened on the click itself, so browsers never block it. */
export const useOpenCallInNewTab = () => {
  const { t } = useTranslation()
  const [outcome, setOutcome] = useState<ControlTowerActionOutcome | null>(null)

  const closeWithFailure = (tab: Window | null, code?: string) => {
    tab?.close()
    setOutcome({ severity: 'error', message: describeJoinCallFailure(code, t) })
  }

  const openWhenLinked = (requestLink: (callbacks: CallLinkCallbacks) => void, openedMessage?: string) => {
    const tab = window.open('about:blank', '_blank')

    requestLink({
      onSuccess: (response) => {
        const hasRoomLink = response.success && !!response.data.roomUrl

        if (!hasRoomLink) {
          closeWithFailure(tab, response.error)
          return
        }

        showCallInTab(tab, response.data.roomUrl)
        if (openedMessage) setOutcome({ severity: 'success', message: openedMessage })
      },
      onError: (error) => closeWithFailure(tab, error.message)
    })
  }

  return { openWhenLinked, outcome, clearOutcome: () => setOutcome(null) }
}
